<template>
  <div id="app">
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="/" aria-label="Renovation Paths home" @click.prevent="navigate('/')">
          <span class="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 48 48" role="img">
              <path class="logo-house" d="M7 22.5 24 8l17 14.5M11.5 20.5V40h25V20.5" />
              <path class="logo-path" d="M18 40c0-7.5 12-8.5 12-15 0-3.5-2.2-5.2-6-5.2" />
            </svg>
          </span>
          <span class="brand-name">
            <strong>Renovation <b>Paths</b></strong>
            <small>Measure. Budget. Build.</small>
          </span>
        </a>

        <button
          class="menu-button"
          type="button"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          aria-controls="primary-navigation"
          :aria-label="mobileOpen ? 'Close navigation' : 'Open navigation'"
          :title="mobileOpen ? 'Close navigation' : 'Open navigation'"
          @click="mobileOpen = !mobileOpen"
        >
          <XIcon v-if="mobileOpen" aria-hidden="true" />
          <MenuIcon v-else aria-hidden="true" />
        </button>

        <nav id="primary-navigation" class="main-nav" :class="{ open: mobileOpen }" aria-label="Primary navigation">
          <a href="/calculators" @click.prevent="navigate('/calculators')">Calculators</a>
          <a href="/guides" @click.prevent="navigate('/guides')">Guides</a>
          <a href="/materials" @click.prevent="navigate('/materials')">Materials</a>
          <a href="/cost-guides" @click.prevent="navigate('/cost-guides')">Cost Guides</a>
          <a href="/faq" @click.prevent="navigate('/faq')">FAQ</a>
          <a class="nav-action" href="/calculators" @click.prevent="navigate('/calculators')">
            <CalculatorIcon aria-hidden="true" />
            Start planning
          </a>
        </nav>
      </div>
    </header>

    <main>
      <section v-if="route.type === 'home'" class="hero-section">
        <div class="hero-copy">
          <p class="eyebrow hero-eyebrow">Practical planning for real projects</p>
          <h1>Free Home Improvement Calculators &amp; Planning Tools</h1>
          <p class="lead">
            Helping homeowners calculate renovation materials, estimate project costs, and plan home improvement projects with practical guidance.
          </p>
          <div class="hero-actions">
            <a class="button primary" href="/calculators" @click.prevent="navigate('/calculators')">
              Explore calculators
              <ArrowRightIcon aria-hidden="true" />
            </a>
            <a class="button hero-secondary" href="/cost-guides" @click.prevent="navigate('/cost-guides')">Compare project costs</a>
          </div>
          <div class="hero-proof" aria-label="Site benefits">
            <span><CheckCircle2Icon aria-hidden="true" /> No sign-up</span>
            <span><CheckCircle2Icon aria-hidden="true" /> Clear formulas</span>
            <span><CheckCircle2Icon aria-hidden="true" /> Practical guidance</span>
          </div>
        </div>
      </section>

      <section v-if="route.type === 'home'" class="estimator-section">
        <div class="estimator-intro">
          <p class="section-kicker">Start with a working number</p>
          <h2>Build a quick budget before you build the room</h2>
          <p>Use a rough cost per square foot and a contingency allowance to create an early planning range. Refine it later with our project-specific cost guides.</p>
          <div class="estimator-points">
            <span><RulerIcon aria-hidden="true" /> Size the scope</span>
            <span><BadgeDollarSignIcon aria-hidden="true" /> Allow for surprises</span>
            <span><BookOpenIcon aria-hidden="true" /> Compare detailed guides</span>
          </div>
        </div>
        <div class="quick-estimator" aria-label="Quick renovation cost estimator">
          <div class="estimator-title">
            <span class="tool-icon"><CalculatorIcon aria-hidden="true" /></span>
            <div>
              <p class="section-kicker">Cost estimator</p>
              <h2>Quick project budget</h2>
            </div>
          </div>
          <div class="compact-form">
            <label>
              Project area
              <span>
                <input v-model.number="homeBudget.area" type="number" min="0" step="10">
                <em>sq ft</em>
              </span>
            </label>
            <label>
              Cost per sq ft
              <span>
                <input v-model.number="homeBudget.costPerSqFt" type="number" min="0" step="5">
                <em>$</em>
              </span>
            </label>
            <label>
              Contingency
              <span>
                <input v-model.number="homeBudget.contingency" type="number" min="0" step="1">
                <em>%</em>
              </span>
            </label>
          </div>
          <div class="estimator-result">
            <span>Planning total</span>
            <strong>{{ homeEstimate.total }}</strong>
            <small>Base {{ homeEstimate.base }} plus contingency {{ homeEstimate.contingency }}</small>
          </div>
        </div>
      </section>

      <section v-if="route.type === 'home'" class="content-band">
        <div class="metric-grid">
          <div>
            <CalculatorIcon aria-hidden="true" />
            <strong>{{ calculators.length }}</strong>
            <span>calculators</span>
          </div>
          <div>
            <BookOpenIcon aria-hidden="true" />
            <strong>{{ guides.length }}</strong>
            <span>planning guides</span>
          </div>
          <div>
            <Layers3Icon aria-hidden="true" />
            <strong>{{ materials.length }}</strong>
            <span>materials</span>
          </div>
          <div>
            <BadgeDollarSignIcon aria-hidden="true" />
            <strong>{{ costGuides.length }}</strong>
            <span>cost guides</span>
          </div>
        </div>
      </section>

      <section v-if="route.type === 'home'" class="content-section">
        <div class="section-heading">
          <p class="section-kicker">Popular Calculators</p>
          <h2>Start with the numbers that shape the project</h2>
        </div>
        <div class="card-grid">
          <a
            v-for="item in popularCalculators"
            :key="item.slug"
            class="resource-card"
            :href="item.url"
            @click.prevent="navigate(item.url)"
          >
            <div class="card-topline">
              <span class="card-icon"><RulerIcon aria-hidden="true" /></span>
              <span>{{ item.category }}</span>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <strong class="card-link">Open calculator <ArrowRightIcon aria-hidden="true" /></strong>
          </a>
        </div>
      </section>

      <section v-if="route.type === 'home'" class="content-section split-section">
        <div>
          <div class="section-heading">
            <p class="section-kicker">Featured Guides</p>
            <h2>Plan scope, materials, and sequencing before you buy</h2>
          </div>
          <div class="stack-list">
            <a
              v-for="item in featuredGuides"
              :key="item.slug"
              :href="`/guides/${item.slug}`"
              @click.prevent="navigate(`/guides/${item.slug}`)"
            >
              <strong>{{ item.title }}</strong>
              <span>{{ item.summary }}</span>
            </a>
          </div>
        </div>
        <div>
          <div class="section-heading">
            <p class="section-kicker">Material Database</p>
            <h2>Compare durability, upkeep, and best uses</h2>
          </div>
          <div class="material-pills">
            <a
              v-for="item in materials.slice(0, 12)"
              :key="item.slug"
              :href="`/materials/${item.slug}`"
              @click.prevent="navigate(`/materials/${item.slug}`)"
            >
              {{ item.title }}
            </a>
          </div>
        </div>
      </section>

      <section v-if="route.type === 'home'" class="content-section">
        <div class="section-heading">
          <p class="section-kicker">FAQ</p>
          <h2>Common homeowner questions</h2>
        </div>
        <div class="faq-grid">
          <article v-for="item in faqs.slice(0, 6)" :key="item.slug" class="faq-card">
            <h3>{{ item.question }}</h3>
            <p>{{ item.answer }}</p>
            <a :href="`/faq/${item.slug}`" @click.prevent="navigate(`/faq/${item.slug}`)">Read answer</a>
          </article>
        </div>
      </section>

      <section v-if="route.type === 'calculators'" class="page-shell">
        <PageIntro
          eyebrow="Calculators"
          title="Home Improvement Calculators"
          description="Use these practical tools to estimate paint, flooring, tile, concrete, outdoor materials, roofing quantities, room size, and remodeling budgets."
        />
        <div class="grouped-layout">
          <section v-for="group in groupedCalculators" :key="group.name" class="resource-group">
            <h2>{{ group.name }}</h2>
            <div class="card-grid">
              <a
                v-for="item in group.items"
                :key="item.slug"
                class="resource-card"
                :href="`/calculators/${item.slug}`"
                @click.prevent="navigate(`/calculators/${item.slug}`)"
              >
                <span>{{ item.category }}</span>
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
              </a>
            </div>
          </section>
        </div>
      </section>

      <section v-if="route.type === 'calculator' && route.item" class="page-shell detail-page">
        <Breadcrumbs :items="breadcrumbs" @navigate="navigate" />
        <PageIntro :eyebrow="route.item.category" :title="route.item.title" :description="route.item.description" />
        <div class="detail-layout">
          <article class="main-article">
            <div class="image-slot">
              <span>{{ route.item.category }}</span>
              <strong>{{ route.item.title }}</strong>
            </div>

            <div class="content-meta" aria-label="Content information">
              <span>Updated July 22, 2026</span>
              <span>Calculation method tested with worked examples</span>
              <a href="/editorial-standards" @click.prevent="navigate('/editorial-standards')">Editorial standards</a>
            </div>

            <section class="tool-surface">
              <div class="tool-heading">
                <div>
                  <p class="section-kicker">Calculator</p>
                  <h2>Enter your project details</h2>
                </div>
                <p>{{ route.item.processIntro }}</p>
              </div>

              <div class="input-grid">
                <label v-for="field in route.item.fields" :key="field.key">
                  {{ field.label }}
                  <span>
                    <input
                      v-model.number="calculatorForms[route.item.slug][field.key]"
                      type="number"
                      :min="field.min"
                      :step="field.step"
                    >
                    <em>{{ field.unit }}</em>
                  </span>
                </label>
              </div>

              <div class="result-panel">
                <span>{{ calculatorResult.primaryLabel }}</span>
                <strong>{{ calculatorResult.primaryValue }}</strong>
                <p>{{ calculatorResult.summary }}</p>
              </div>

              <div class="result-grid">
                <div v-for="item in calculatorResult.results" :key="item.label">
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                </div>
              </div>
            </section>

            <section class="article-section">
              <h2>Calculation Process</h2>
              <ol class="step-list">
                <li v-for="step in calculatorResult.steps" :key="step">{{ step }}</li>
              </ol>
            </section>

            <section class="article-section">
              <h2>What The Result Means</h2>
              <p>{{ route.item.resultMeaning }}</p>
              <div v-if="route.item.tips.length" class="note-list">
                <p v-for="tip in route.item.tips" :key="tip">{{ tip }}</p>
              </div>
            </section>

            <section class="article-section">
              <h2>Practical Example</h2>
              <p>{{ route.item.example }}</p>
            </section>

            <section class="article-section">
              <h2>Assumptions And Limits</h2>
              <p>{{ calculatorAssumption(route.item) }}</p>
              <p>{{ calculatorCategoryNote(route.item.category) }}</p>
              <ul class="clean-list">
                <li>Measure each project area separately and keep every input in the unit shown beside the field.</li>
                <li>Replace default coverage rates, package sizes, prices, and waste percentages with the selected product information.</li>
                <li>Confirm structural, electrical, plumbing, waterproofing, permit, and safety decisions with qualified local professionals.</li>
              </ul>
            </section>

            <FaqBlock :faqs="route.item.faqs" />
            <RelatedBlock :items="relatedItems(route.item.related)" @navigate="navigate" />
          </article>

          <aside class="side-rail">
            <h2>Related Tools</h2>
            <a
              v-for="item in relatedItems(route.item.related)"
              :key="item.url"
              :href="item.url"
              @click.prevent="navigate(item.url)"
            >
              <span>{{ item.type }}</span>
              <strong>{{ item.title }}</strong>
            </a>
          </aside>
        </div>
      </section>

      <section v-if="route.type === 'guides'" class="page-shell">
        <PageIntro
          eyebrow="Guides"
          title="Home Improvement Guides"
          description="Readable planning guides for painting, flooring, bathrooms, kitchens, outdoor projects, roofing, budgeting, permits, and maintenance."
        />
        <div class="grouped-layout">
          <section v-for="group in groupedGuides" :key="group.name" class="resource-group">
            <h2>{{ group.name }}</h2>
            <div class="card-grid">
              <a
                v-for="item in group.items"
                :key="item.slug"
                class="resource-card"
                :href="`/guides/${item.slug}`"
                @click.prevent="navigate(`/guides/${item.slug}`)"
              >
                <span>{{ item.category }}</span>
                <h3>{{ item.title }}</h3>
                <p>{{ item.summary }}</p>
              </a>
            </div>
          </section>
        </div>
      </section>

      <section v-if="route.type === 'guide' && route.item" class="page-shell detail-page">
        <Breadcrumbs :items="breadcrumbs" @navigate="navigate" />
        <PageIntro :eyebrow="route.item.category" :title="route.item.title" :description="route.item.summary" />
        <article class="main-article single-column">
          <div class="image-slot">
            <span>{{ route.item.category }}</span>
            <strong>{{ route.item.title }}</strong>
          </div>
          <div class="content-meta" aria-label="Content information">
            <span>Updated July 22, 2026</span>
            <span>Independent planning guide</span>
            <a href="/editorial-standards" @click.prevent="navigate('/editorial-standards')">Editorial standards</a>
          </div>
          <section class="article-section">
            <h2>Project Introduction</h2>
            <p>{{ route.item.summary }}</p>
          </section>
          <section class="article-section">
            <h2>Before You Start</h2>
            <p>{{ guideBeforeStart(route.item) }}</p>
            <p>{{ guideProfessionalNote(route.item.category) }}</p>
          </section>
          <section class="article-section">
            <h2>Planning Steps</h2>
            <ol class="step-list">
              <li v-for="step in route.item.steps" :key="step">{{ step }}</li>
            </ol>
          </section>
          <section class="article-section">
            <h2>Decisions That Change The Plan</h2>
            <ul class="clean-list">
              <li v-for="decision in guideDecisionPoints(route.item)" :key="decision">{{ decision }}</li>
            </ul>
          </section>
          <section class="article-section">
            <h2>Materials To Consider</h2>
            <ul class="clean-list">
              <li v-for="item in route.item.materials" :key="item">{{ item }}</li>
            </ul>
          </section>
          <section class="article-section">
            <h2>Cost Notes</h2>
            <p>{{ route.item.costNotes }}</p>
            <p>Request written quotes that use the same scope, product quality, preparation, disposal, and finish assumptions. A lower total is not directly comparable when important work is excluded.</p>
          </section>
          <section class="article-section">
            <h2>Final Planning Check</h2>
            <ul class="clean-list">
              <li>Record measurements, selected products, quantities, and finish choices in one project document.</li>
              <li>Confirm lead times and inspect delivered materials before demolition or irreversible work begins.</li>
              <li>Identify permit, inspection, utility shutoff, ventilation, moisture, and occupant-safety requirements.</li>
              <li>Keep a contingency for concealed conditions and document changes before approving additional work.</li>
            </ul>
          </section>
          <FaqBlock :faqs="route.item.faqs" />
          <RelatedBlock :items="relatedItems(route.item.related)" @navigate="navigate" />
        </article>
      </section>

      <section v-if="route.type === 'materials'" class="page-shell">
        <PageIntro
          eyebrow="Material Database"
          title="Home Improvement Material Database"
          description="Compare flooring, paint, tile, construction, outdoor, roofing, and countertop materials before you commit to a project."
        />
        <div class="grouped-layout">
          <section v-for="group in groupedMaterials" :key="group.name" class="resource-group">
            <h2>{{ group.name }}</h2>
            <div class="card-grid">
              <a
                v-for="item in group.items"
                :key="item.slug"
                class="resource-card"
                :href="`/materials/${item.slug}`"
                @click.prevent="navigate(`/materials/${item.slug}`)"
              >
                <span>{{ item.category }}</span>
                <h3>{{ item.title }}</h3>
                <p>{{ item.overview }}</p>
              </a>
            </div>
          </section>
        </div>
      </section>

      <section v-if="route.type === 'material' && route.item" class="page-shell detail-page">
        <Breadcrumbs :items="breadcrumbs" @navigate="navigate" />
        <PageIntro :eyebrow="route.item.category" :title="route.item.title" :description="route.item.overview" />
        <article class="main-article single-column">
          <div class="image-slot">
            <span>{{ route.item.category }}</span>
            <strong>{{ route.item.title }}</strong>
          </div>
          <div class="content-meta" aria-label="Content information">
            <span>Updated July 22, 2026</span>
            <span>Material comparison reference</span>
            <a href="/editorial-standards" @click.prevent="navigate('/editorial-standards')">Editorial standards</a>
          </div>
          <section class="fact-grid">
            <div>
              <span>Average Cost</span>
              <strong>{{ route.item.averageCost }}</strong>
            </div>
            <div>
              <span>Durability</span>
              <strong>{{ route.item.durability }}</strong>
            </div>
          </section>
          <section class="article-section">
            <h2>Overview</h2>
            <p>{{ route.item.overview }}</p>
          </section>
          <section class="article-section">
            <h2>How To Decide If It Fits Your Project</h2>
            <p>{{ materialDecisionGuide(route.item) }}</p>
            <ul class="clean-list">
              <li v-for="question in materialBuyingQuestions(route.item)" :key="question">{{ question }}</li>
            </ul>
          </section>
          <section class="comparison-grid">
            <div>
              <h2>Advantages</h2>
              <ul class="clean-list">
                <li v-for="advantage in route.item.advantages" :key="advantage">{{ advantage }}</li>
              </ul>
            </div>
            <div>
              <h2>Disadvantages</h2>
              <ul class="clean-list">
                <li v-for="disadvantage in route.item.disadvantages" :key="disadvantage">{{ disadvantage }}</li>
              </ul>
            </div>
          </section>
          <section class="article-section">
            <h2>Maintenance</h2>
            <p>{{ route.item.maintenance }}</p>
            <p>{{ materialCategoryNote(route.item.category) }}</p>
          </section>
          <section class="article-section">
            <h2>Best Use Cases</h2>
            <ul class="clean-list">
              <li v-for="useCase in route.item.bestUseCases" :key="useCase">{{ useCase }}</li>
            </ul>
          </section>
          <section class="article-section">
            <h2>Before You Buy</h2>
            <ul class="clean-list">
              <li>Request a current product data sheet, warranty, installation instructions, and maintenance requirements.</li>
              <li>Price the complete installed assembly, including preparation, accessories, fasteners, trim, waste, delivery, and disposal.</li>
              <li>Order a sample and evaluate color, texture, cleaning, and transitions in the actual room or exterior light.</li>
              <li>Confirm that the product is suitable for the substrate, moisture exposure, temperature range, and local code conditions.</li>
            </ul>
          </section>
          <RelatedBlock :items="relatedItems(route.item.related)" @navigate="navigate" />
        </article>
      </section>

      <section v-if="route.type === 'costs'" class="page-shell">
        <PageIntro
          eyebrow="Cost Guides"
          title="Home Renovation Cost Guides"
          description="Use these cost pages to understand average project ranges, material and labor line items, price factors, budget tips, and related planning tools."
        />
        <div class="card-grid">
          <a
            v-for="item in costGuides"
            :key="item.slug"
            class="resource-card"
            :href="`/cost-guides/${item.slug}`"
            @click.prevent="navigate(`/cost-guides/${item.slug}`)"
          >
            <span>{{ item.category }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.averageCost }}</p>
          </a>
        </div>
      </section>

      <section v-if="route.type === 'cost' && route.item" class="page-shell detail-page">
        <Breadcrumbs :items="breadcrumbs" @navigate="navigate" />
        <PageIntro :eyebrow="route.item.category" :title="route.item.title" :description="route.item.metaDescription" />
        <article class="main-article single-column">
          <div class="image-slot">
            <span>{{ route.item.category }}</span>
            <strong>{{ route.item.title }}</strong>
          </div>
          <div class="content-meta" aria-label="Content information">
            <span>Updated July 22, 2026</span>
            <span>Broad U.S. planning range, not a quote</span>
            <a href="/editorial-standards" @click.prevent="navigate('/editorial-standards')">Cost methodology</a>
          </div>
          <section class="fact-grid">
            <div>
              <span>Average Cost</span>
              <strong>{{ route.item.averageCost }}</strong>
            </div>
            <div>
              <span>Material Cost</span>
              <strong>{{ route.item.materialCost }}</strong>
            </div>
            <div>
              <span>Labor Cost</span>
              <strong>{{ route.item.laborCost }}</strong>
            </div>
          </section>
          <section class="article-section">
            <h2>How To Use This Cost Range</h2>
            <p>{{ costRangeExplanation(route.item) }}</p>
            <p>Use the range to test early scope decisions, not to select a contractor or authorize work. Replace broad allowances with current local supplier prices and comparable written quotes before committing funds.</p>
          </section>
          <section class="article-section">
            <h2>What The Estimate Assumes</h2>
            <ul class="clean-list">
              <li v-for="assumption in costAssumptions(route.item)" :key="assumption">{{ assumption }}</li>
            </ul>
          </section>
          <section class="article-section">
            <h2>Factors Affecting Price</h2>
            <ul class="clean-list">
              <li v-for="factor in route.item.factors" :key="factor">{{ factor }}</li>
            </ul>
          </section>
          <section class="article-section">
            <h2>Budget Tips</h2>
            <ul class="clean-list">
              <li v-for="tip in route.item.budgetTips" :key="tip">{{ tip }}</li>
            </ul>
          </section>
          <section class="article-section">
            <h2>Compare Contractor Quotes Fairly</h2>
            <ul class="clean-list">
              <li>Give each bidder the same measurements, drawings, product selections, and requested finish level.</li>
              <li>Separate allowances from fixed prices and confirm taxes, delivery, demolition, protection, cleanup, and disposal.</li>
              <li>Check who is responsible for permits, inspections, design, temporary services, and concealed-condition repairs.</li>
              <li>Compare payment schedules, change-order rules, warranty terms, insurance, and exclusions as well as total price.</li>
              <li>Keep contingency outside the contract amount until a documented condition or approved scope change requires it.</li>
            </ul>
          </section>
          <FaqBlock :faqs="route.item.faqs" />
          <RelatedBlock :items="relatedItems(route.item.related)" @navigate="navigate" />
        </article>
      </section>

      <section v-if="route.type === 'faq'" class="page-shell">
        <PageIntro
          eyebrow="FAQ Knowledge Base"
          title="Home Improvement FAQ"
          description="Short, practical answers about materials, costs, installation, maintenance, budgeting, and planning."
        />
        <div class="faq-grid wide">
          <article v-for="item in faqs" :key="item.slug" class="faq-card">
            <span>{{ item.category }}</span>
            <h3>{{ item.question }}</h3>
            <p>{{ item.answer }}</p>
            <a :href="`/faq/${item.slug}`" @click.prevent="navigate(`/faq/${item.slug}`)">Open question</a>
          </article>
        </div>
      </section>

      <section v-if="route.type === 'faqDetail' && route.item" class="page-shell detail-page">
        <Breadcrumbs :items="breadcrumbs" @navigate="navigate" />
        <PageIntro :eyebrow="route.item.category" :title="route.item.question" :description="route.item.answer" />
        <article class="main-article single-column">
          <section class="article-section">
            <h2>Short Answer</h2>
            <p>{{ route.item.answer }}</p>
          </section>
          <section class="article-section">
            <h2>Details</h2>
            <ul class="clean-list">
              <li v-for="detail in route.item.details" :key="detail">{{ detail }}</li>
            </ul>
          </section>
          <RelatedBlock :items="relatedItems(route.item.related)" @navigate="navigate" />
        </article>
      </section>

      <section v-if="route.type === 'about'" class="page-shell legal-page">
        <PageIntro
          eyebrow="About"
          title="About Renovation Paths"
          description="Renovation Paths helps homeowners plan projects with calculators, material references, cost guides, and practical renovation knowledge."
        />
        <article class="main-article single-column">
          <p class="content-meta">Last reviewed July 22, 2026</p>
          <section class="article-section">
            <h2>Our Purpose</h2>
            <p>
              Renovation Paths is an English home improvement resource built for practical planning. The site combines calculators, cost ranges, material notes, and project guides so homeowners can make better early decisions before they request quotes or purchase materials.
            </p>
            <p>
              Our estimates are meant for planning, comparison, and conversation with contractors. Local codes, product instructions, building conditions, and professional judgment should guide final decisions.
            </p>
          </section>
          <section class="article-section">
            <h2>What We Publish</h2>
            <ul class="clean-list">
              <li>Interactive calculators for paint, flooring, tile, concrete, roofing, outdoor projects, and remodeling budgets.</li>
              <li>Material database pages that compare cost, durability, advantages, disadvantages, maintenance, and best use cases.</li>
              <li>Cost guides that explain average ranges, material cost, labor cost, price factors, and budget tips.</li>
              <li>FAQ answers written for homeowners who need clear planning context.</li>
            </ul>
          </section>
          <section class="article-section">
            <h2>How The Site Is Maintained</h2>
            <p>
              Renovation Paths is independently published and maintained as a planning resource. Calculator logic is checked against worked examples before publication. Guides are organized around the decisions a homeowner normally faces: scope, measurements, material selection, labor, safety, and the point at which a licensed professional should be involved.
            </p>
            <p>
              Cost figures are broad U.S. planning ranges rather than live local quotes. They are reviewed for internal consistency and paired with scope notes so readers can compare estimates on a like-for-like basis. Read our <a href="/editorial-standards" @click.prevent="navigate('/editorial-standards')">Editorial Standards And Methodology</a> for the full approach.
            </p>
          </section>
          <section class="article-section">
            <h2>Independence And Corrections</h2>
            <p>
              Renovation Paths does not provide contractor bids, code approval, engineering services, or product warranties. The site currently does not publish paid rankings or affiliate product placements. If commercial relationships are introduced later, they will be clearly disclosed without changing our calculation results.
            </p>
            <p>
              Readers can report an unclear formula, outdated range, broken link, or factual concern through the <a href="/contact" @click.prevent="navigate('/contact')">contact page</a> or by emailing <a :href="`mailto:${site.contactEmail}`">{{ site.contactEmail }}</a>. Corrections that affect a recommendation or result are reviewed and incorporated into the relevant page.
            </p>
          </section>
        </article>
      </section>

      <section v-if="route.type === 'editorial'" class="page-shell legal-page">
        <PageIntro
          eyebrow="Methodology"
          title="Editorial Standards And Methodology"
          description="How Renovation Paths creates, tests, updates, and corrects its calculators, cost ranges, material references, and project guides."
        />
        <article class="main-article single-column">
          <p class="content-meta">Effective and last reviewed July 22, 2026</p>
          <section class="article-section">
            <h2>Purpose And Audience</h2>
            <p>
              Renovation Paths is written for homeowners and renters making early project decisions. Our aim is to turn measurements, product specifications, and budget assumptions into a practical first plan. The site is educational: it helps readers prepare questions and compare options, but it does not replace an on-site inspection, a manufacturer instruction, a permit authority, or a licensed trade professional.
            </p>
          </section>
          <section class="article-section">
            <h2>Calculator Method</h2>
            <p>
              Each calculator starts with a stated geometric or cost relationship, uses only the measurements entered by the user, and shows the arithmetic behind the result. Outputs are rounded to a purchasing-friendly unit where appropriate. Waste allowances, coverage rates, unit prices, and contingency percentages remain visible inputs so readers can replace our examples with values from the actual product label or quote.
            </p>
            <p>
              Before publication, calculation paths are tested with worked examples, boundary values, and zero inputs. A calculator cannot account for every doorway, pattern repeat, substrate condition, roof detail, local code, or package size. The page therefore explains assumptions and tells readers what to verify before ordering.
            </p>
          </section>
          <section class="article-section">
            <h2>Cost Range Method</h2>
            <p>
              Cost pages provide broad U.S. planning ranges in U.S. dollars. They are not live market prices or location-specific bids. A range is presented with separate material and labor context, scope factors, and exclusions because two projects with the same room size may have very different demolition, access, finish, permit, disposal, and repair requirements.
            </p>
            <p>
              Readers should compare at least three written local quotes when the project is substantial. The comparison should use the same scope, allowances, taxes, delivery, protection, cleanup, permit responsibility, payment schedule, change-order process, insurance, and warranty terms. Published ranges are reviewed when the related page is revised, and each detailed page displays its review date.
            </p>
          </section>
          <section class="article-section">
            <h2>Guides And Material References</h2>
            <p>
              Guides are structured around preparation, sequence, materials, cost, common failure points, and handoff to a professional. Material pages compare cost, expected service demands, advantages, limitations, maintenance, and suitable locations. We avoid declaring one universal "best" product; moisture, wear, substrate, climate, household needs, repairability, and installation skill all affect the choice.
            </p>
          </section>
          <section class="article-section">
            <h2>Independence, Safety, And Corrections</h2>
            <p>
              The site currently has no sponsored rankings or affiliate product placements. Advertising, when introduced, will be visually separate from editorial tools and will not determine a result or recommendation. Renovation Paths does not accept payment to alter calculator formulas or hide material disadvantages.
            </p>
            <p>
              Work involving structure, gas, electrical service, hazardous materials, fall protection, major plumbing, or permits may require a licensed professional. Readers should follow local rules and product safety instructions. To request a correction, email <a :href="`mailto:${site.contactEmail}`">{{ site.contactEmail }}</a> and identify the page, statement, and supporting detail. We review corrections for clarity, formula impact, and related pages before updating the content.
            </p>
          </section>
        </article>
      </section>

      <section v-if="route.type === 'contact'" class="page-shell legal-page">
        <PageIntro
          eyebrow="Contact"
          title="Contact Renovation Paths"
          :description="`Questions, corrections, and partnership messages can be sent to ${site.contactEmail}.`"
        />
        <article class="main-article single-column">
          <section class="tool-surface">
            <h2>Send a message</h2>
            <form class="contact-form" @submit.prevent="submitContact">
              <label>
                Name
                <input v-model.trim="contact.name" type="text" required maxlength="120" autocomplete="name">
              </label>
              <label>
                Email
                <input v-model.trim="contact.email" type="email" required maxlength="160" autocomplete="email">
              </label>
              <label>
                Topic
                <input v-model.trim="contact.topic" type="text" required maxlength="180">
              </label>
              <label>
                Message
                <textarea v-model.trim="contact.message" required maxlength="2000" rows="6"></textarea>
              </label>
              <label class="honeypot-field" aria-hidden="true">
                Website
                <input v-model.trim="contact.website" type="text" tabindex="-1" autocomplete="off">
              </label>
              <button class="button primary" type="submit" :disabled="contactSending">
                {{ contactSending ? 'Sending...' : 'Send message' }}
              </button>
              <p v-if="contactStatus" class="form-status" role="status">{{ contactStatus }}</p>
            </form>
          </section>
          <section class="article-section">
            <h2>Email Instead</h2>
            <p>
              If the form is unavailable, send questions, corrections, calculator feedback, or business inquiries directly to our published contact address.
            </p>
            <p><a :href="`mailto:${site.contactEmail}`">{{ site.contactEmail }}</a></p>
          </section>
        </article>
      </section>

      <section v-if="route.type === 'privacy'" class="page-shell legal-page">
        <PageIntro
          eyebrow="Privacy"
          title="Privacy Policy"
          description="This policy explains how Renovation Paths handles contact messages, cookies, analytics, Google AdSense, and third-party advertising."
        />
        <article class="main-article single-column">
          <p class="content-meta">Effective and last reviewed July 22, 2026</p>
          <section class="article-section">
            <h2>Information We Collect</h2>
            <p>
              Renovation Paths may collect information you submit through the contact form, including your name, email address, topic, and message. If you choose to email us instead, your email address, message, and any information you include are handled by the relevant email providers. Server logs may include browser type, IP address, referring page, date, and visited URLs for security and performance analysis.
            </p>
            <p>
              Calculator inputs are processed in your browser and are not attached to an account. Please do not submit sensitive personal, financial, health, government identification, or precise location information through the form or by email.
            </p>
          </section>
          <section class="article-section">
            <h2>How Information Is Used And Retained</h2>
            <p>
              Contact information is used to answer the message, investigate a reported problem, prevent abuse, and maintain a record of the conversation. Technical logs may be used to secure the service, diagnose errors, understand aggregate performance, and protect the site from automated misuse. We do not sell contact-form or correspondence information.
            </p>
            <p>
              Contact messages are retained only as long as reasonably needed to respond, maintain necessary business records, or meet legal and security obligations. Log retention is determined by the hosting provider and operational need. Reasonable administrative and technical safeguards are used, but no Internet transmission or storage system can be guaranteed completely secure.
            </p>
          </section>
          <section class="article-section">
            <h2>Google AdSense And Third-Party Advertising</h2>
            <p>
              Renovation Paths does not currently load Google AdSense ads in this pre-application version. If advertising is enabled, third-party vendors, including Google, may use cookies, web beacons, IP addresses, and similar technologies to serve, measure, and personalize ads. Google's advertising cookies may allow Google and its partners to serve ads based on visits to this site and other sites on the Internet.
            </p>
            <p>
              Google explains how it uses information from sites that use its services on its <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener">partner sites and apps page</a>. Users may manage personalized advertising in <a href="https://adssettings.google.com/" target="_blank" rel="noopener">Google Ads Settings</a> and review industry opt-out choices at <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener">aboutads.info</a>. We will not send information to Google that Google could recognize as personally identifiable information for ad targeting or reporting.
            </p>
          </section>
          <section class="article-section">
            <h2>Cookies And Analytics</h2>
            <p>
              Essential storage may remember basic browser-side preferences. If analytics is introduced, it may collect pseudonymous device, browser, referral, and page interaction information to understand performance and navigation. Advertising cookies may support ad delivery, frequency control, measurement, fraud prevention, and personalization. The exact cookies present can change as providers update their services; this policy and the consent controls will be updated when those services are enabled.
            </p>
            <p>
              Our hosting infrastructure may set a short-lived, HttpOnly session or routing cookie to deliver requests consistently, protect the service, and support basic security controls. This operational cookie is not used by Renovation Paths to build an advertising profile. Browser controls can remove or block cookies, although doing so may affect routing or future consent preferences.
            </p>
          </section>
          <section class="article-section">
            <h2>Consent And Your Choices</h2>
            <p>
              Before Google advertising is served to users in the European Economic Area, the United Kingdom, or Switzerland, Renovation Paths will implement a Google-certified consent management platform. Where required, visitors will be able to accept, reject, or manage consent before advertising cookies or personalized ad processing begins. Visitors can later revisit their privacy choices through the consent control displayed on the site.
            </p>
            <p>
              Depending on your location, you may ask to access, correct, or delete personal information submitted through the form or sent in correspondence, or object to certain processing. Email the address below with enough context to identify your message. We may need to retain limited information when required for security, recordkeeping, or legal compliance.
            </p>
          </section>
          <section class="article-section">
            <h2>Children And External Links</h2>
            <p>
              Renovation Paths is a general-audience home improvement site and is not directed to children under 13. We do not knowingly collect personal information from children. Links to manufacturers, regulators, opt-out services, or other third parties lead to services governed by their own privacy policies.
            </p>
          </section>
          <section class="article-section">
            <h2>Contact</h2>
            <p>Privacy questions and data requests can be sent to <a :href="`mailto:${site.contactEmail}`">{{ site.contactEmail }}</a>. This policy may be updated when site features, vendors, or legal requirements change; the review date at the top will identify the current version.</p>
          </section>
        </article>
      </section>

      <section v-if="route.type === 'terms'" class="page-shell legal-page">
        <PageIntro
          eyebrow="Terms"
          title="Terms of Service"
          description="These terms explain how visitors may use Renovation Paths calculators, guides, cost data, and contact features."
        />
        <article class="main-article single-column">
          <p class="content-meta">Effective and last reviewed July 22, 2026</p>
          <section class="article-section">
            <h2>Use Of The Site</h2>
            <p>
              Renovation Paths provides calculators and planning information for general home improvement education. Results are estimates, not bids, professional engineering advice, code approval, or a substitute for contractor measurement.
            </p>
          </section>
          <section class="article-section">
            <h2>Project Responsibility</h2>
            <p>
              Users are responsible for verifying measurements, product instructions, local building codes, permits, safety requirements, and professional recommendations before starting work or buying materials.
            </p>
          </section>
          <section class="article-section">
            <h2>Estimates And Professional Advice</h2>
            <p>
              Material quantities and costs depend on the accuracy of the values entered by the user. Coverage rates, waste, package sizes, labor rates, taxes, delivery, demolition, access, substrate repair, permits, disposal, and concealed conditions can materially change a result. Confirm final quantities with the product label or supplier and obtain written local quotes before committing funds.
            </p>
            <p>
              Content is not architectural, engineering, electrical, plumbing, legal, financial, environmental, or safety advice. Work involving structure, utility service, hazardous material, code compliance, or fall risk should be evaluated by an appropriately qualified professional.
            </p>
          </section>
          <section class="article-section">
            <h2>Acceptable Use</h2>
            <p>
              You may use the site for lawful personal planning and reasonable reference. You may not interfere with the service, attempt unauthorized access, submit malicious code, overload endpoints, misrepresent the source of content, or reproduce a substantial portion of the database as a competing service. Automated use must respect published crawler instructions and applicable law.
            </p>
          </section>
          <section class="article-section">
            <h2>Ownership And Third-Party Services</h2>
            <p>
              The site design, original text, calculation presentation, and compiled database are owned by Renovation Paths except where another owner is identified. Product names may be trademarks of their respective owners. Links to third-party sites are provided for context and do not mean that Renovation Paths controls or endorses their content, availability, privacy practices, products, or services.
            </p>
          </section>
          <section class="article-section">
            <h2>Content And Availability</h2>
            <p>
              We may update calculators, cost ranges, guides, and policies as new information becomes available. We try to keep the site useful and accessible, but we do not guarantee uninterrupted availability.
            </p>
          </section>
          <section class="article-section">
            <h2>Disclaimer And Limitation</h2>
            <p>
              To the extent permitted by law, the site is provided "as is" without warranties of accuracy, fitness for a particular purpose, or uninterrupted operation. Renovation Paths is not responsible for purchases, project decisions, property damage, injury, delay, lost savings, contractor performance, or other loss arising from reliance on an estimate or external service. Nothing in these terms excludes a right or liability that cannot lawfully be excluded.
            </p>
          </section>
          <section class="article-section">
            <h2>Changes And Contact</h2>
            <p>
              Continued use after a posted update means the current terms apply to later use. Questions about these terms can be sent to <a :href="`mailto:${site.contactEmail}`">{{ site.contactEmail }}</a>. If any provision is found unenforceable, the remaining provisions continue to apply.
            </p>
          </section>
        </article>
      </section>

      <section v-if="route.type === 'notFound'" class="page-shell">
        <PageIntro
          eyebrow="404"
          title="Page Not Found"
          description="The page you requested was not found. Use the navigation to find calculators, guides, materials, cost guides, or FAQ answers."
        />
        <div class="hero-actions">
          <a class="button primary" href="/calculators" @click.prevent="navigate('/calculators')">Browse calculators</a>
          <a class="button secondary" href="/" @click.prevent="navigate('/')">Return home</a>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="footer-brand">
        <span class="brand-mark" aria-hidden="true">
          <svg viewBox="0 0 48 48" role="img">
            <path class="logo-house" d="M7 22.5 24 8l17 14.5M11.5 20.5V40h25V20.5" />
            <path class="logo-path" d="M18 40c0-7.5 12-8.5 12-15 0-3.5-2.2-5.2-6-5.2" />
          </svg>
        </span>
        <div>
          <strong>Renovation Paths</strong>
          <p>{{ site.tagline }}</p>
        </div>
      </div>
      <nav aria-label="Footer navigation">
        <a href="/about" @click.prevent="navigate('/about')">About Us</a>
        <a href="/editorial-standards" @click.prevent="navigate('/editorial-standards')">Editorial Standards</a>
        <a href="/contact" @click.prevent="navigate('/contact')">Contact</a>
        <a href="/privacy-policy" @click.prevent="navigate('/privacy-policy')">Privacy Policy</a>
        <a href="/terms-of-service" @click.prevent="navigate('/terms-of-service')">Terms of Service</a>
        <a href="/sitemap.xml">Sitemap</a>
      </nav>
    </footer>
  </div>
</template>

<script>
import { runCalculator } from './utils/calculatorEngines'
import {
  ArrowRight as ArrowRightIcon,
  BadgeDollarSign as BadgeDollarSignIcon,
  BookOpen as BookOpenIcon,
  Calculator as CalculatorIcon,
  CheckCircle2 as CheckCircle2Icon,
  Layers3 as Layers3Icon,
  Menu as MenuIcon,
  Ruler as RulerIcon,
  X as XIcon
} from 'lucide-vue'
import './assets/styles.css'

const siteData = require('./data/siteData')

const PageIntro = {
  props: ['eyebrow', 'title', 'description'],
  render(h) {
    return h('div', { class: 'page-intro' }, [
      h('p', { class: 'eyebrow' }, this.eyebrow),
      h('h1', this.title),
      h('p', { class: 'lead' }, this.description)
    ])
  }
}

const Breadcrumbs = {
  props: ['items'],
  render(h) {
    return h('nav', {
      class: 'breadcrumbs',
      attrs: { 'aria-label': 'Breadcrumb' }
    }, this.items.map((item, index) => h('a', {
      key: item.url,
      attrs: { href: item.url },
      on: {
        click: event => {
          event.preventDefault()
          this.$emit('navigate', item.url)
        }
      }
    }, [item.label, index < this.items.length - 1 ? h('span', '/') : null])))
  }
}

const FaqBlock = {
  props: ['faqs'],
  render(h) {
    return h('section', { class: 'article-section' }, [
      h('h2', 'FAQ'),
      h('div', { class: 'faq-list' }, this.faqs.map(item => h('details', {
        key: item.question
      }, [
        h('summary', item.question),
        h('p', item.answer)
      ])))
    ])
  }
}

const RelatedBlock = {
  props: ['items'],
  render(h) {
    if (!this.items.length) return null

    return h('section', { class: 'article-section' }, [
      h('h2', 'Related Articles And Tools'),
      h('div', { class: 'related-grid' }, this.items.map(item => h('a', {
        key: item.url,
        attrs: { href: item.url },
        on: {
          click: event => {
            event.preventDefault()
            this.$emit('navigate', item.url)
          }
        }
      }, [
        h('span', item.type),
        h('strong', item.title),
        h('small', item.description)
      ])))
    ])
  }
}

const normalizePath = path => {
  const withoutQuery = String(path || '/').split('?')[0].split('#')[0]
  const clean = `/${withoutQuery.replace(/^\/+/, '')}`.replace(/\/+$/, '')
  return clean || '/'
}

const groupBy = (items, key) => {
  const groups = []
  items.forEach(item => {
    let group = groups.find(entry => entry.name === item[key])
    if (!group) {
      group = { name: item[key], items: [] }
      groups.push(group)
    }
    group.items.push(item)
  })
  return groups
}

const money = number => `$${Math.round(number).toLocaleString('en-US')}`

const calculatorCategoryNotes = {
  Painting: 'Paint coverage changes with surface texture, porosity, color change, application method, and the number of coats. Treat the coverage rate on the specific product label as the controlling value, and separate primer from finish paint when the products have different spread rates.',
  Flooring: 'Floor plans rarely behave like one perfect rectangle. Measure closets, alcoves, transitions, and fixed obstructions separately. Pattern direction, board length, room squareness, plank defects, and the supplier return policy should guide the final waste allowance and package count.',
  Tile: 'Tile layouts need extra material for perimeter cuts, breakage, future repairs, and pattern alignment. Confirm nominal versus actual tile size, grout-joint width, trim pieces, movement joints, and the substrate preparation system before placing an order.',
  'Construction Materials': 'Bulk-material estimates assume reasonably uniform depth and geometry. Compaction, settlement, excavation shape, moisture, delivery minimums, reinforcement, and site access can change the purchased quantity. Confirm density or yield with the local supplier when ordering by weight.',
  'Outdoor Projects': 'Outdoor quantities depend on layout, grade, frost depth, drainage, wind exposure, soil, property boundaries, and local permit rules. Mark utilities and verify setbacks before digging. Structural spans and footings should follow an approved plan and local code.',
  Roofing: 'Roof work involves fall risk and geometry that may not be visible from the ground. Pitch, valleys, hips, dormers, flashing, underlayment, ventilation, tear-off layers, and local wind or snow requirements all affect quantities. Use professional measurements for purchasing or contracting.',
  'General Home Projects': 'Early budget tools are most useful when the scope is written down first. Separate must-have work from optional finishes, use current local quotes for major trades, and keep contingency outside committed spending until a real condition or approved change requires it.'
}

const professionalNotes = {
  Painting: 'Escalate peeling coatings of unknown age, persistent moisture, extensive mold, lead-paint risk, or high-access work to a qualified professional before surface preparation begins.',
  Flooring: 'Ask a flooring professional to assess structural movement, widespread moisture, asbestos risk, radiant heat compatibility, or a substrate that is outside the flooring manufacturer\'s flatness tolerance.',
  Tile: 'Waterproof showers, exterior assemblies, large-format tile, movement joints, and visibly deflecting floors need system-specific design and careful substrate verification.',
  Bathroom: 'Licensed plumbing and electrical work, ventilation changes, structural alterations, and waterproof shower assemblies often require permits or inspections.',
  Kitchen: 'Cabinet layout changes can affect electrical, plumbing, ventilation, gas, structure, and egress. Coordinate those trades before final product orders.',
  Basement: 'Resolve water entry, radon, egress, ceiling height, fire separation, and structural concerns before covering walls or floors.',
  Roofing: 'Roof access, structural damage, flashing, ventilation, and weather exposure make professional inspection and fall protection especially important.',
  Outdoor: 'Confirm utility locations, property lines, drainage, frost depth, footing design, and required permits before excavation or structural work.',
  Walls: 'Load-bearing changes, hidden utilities, fire-rated assemblies, water damage, and suspected hazardous materials require qualified evaluation.',
  Energy: 'Combustion safety, air sealing near heat sources, electrical loads, moisture control, and ventilation should be assessed as one system.',
  Planning: 'A designer, engineer, contractor, or permit authority should review the plan when scope affects structure, utilities, code compliance, or several trades.'
}

const materialCategoryNotes = {
  'Flooring Materials': 'Use the manufacturer\'s cleaning and moisture instructions. Grit control, felt pads, prompt spill cleanup, humidity management, and compatible cleaners usually matter more than aggressive polishing. Keep spare material from the same production lot for repairs.',
  'Painting Materials': 'Store labeled touch-up material away from heat and freezing conditions. Clean painted surfaces with the mildest method that works, and repair moisture or adhesion problems before recoating. Follow local rules for disposal of leftover coatings and solvents.',
  'Tile Materials': 'Protect grout and movement joints as well as the tile surface. Use a cleaner compatible with the tile, grout, and sealer; acidic or abrasive products can damage some finishes. Repair cracked joints after the cause of movement or moisture is understood.',
  'Construction Materials': 'Keep bagged products dry and off the ground, protect aggregates from contamination, and follow shelf-life and mixing instructions. Cracking, settlement, erosion, or persistent moisture may indicate a base or drainage problem rather than a surface-maintenance issue.',
  'Landscape Materials': 'Recheck depth after initial settlement, keep material away from siding and trunks, and restore drainage paths after storms. Organic products decompose and need periodic replenishment; mineral products may need edging, fabric maintenance, and weed removal.',
  'Outdoor Materials': 'Inspect fasteners, end grain, coatings, drainage gaps, and ground contact each season. Cleaning and refinishing intervals vary with sun, moisture, salt, climate, and product warranty requirements.',
  'Roofing Materials': 'Use safe ground-level observations and scheduled professional inspections after severe weather. Keep gutters and drainage paths clear, address flashing or penetration leaks promptly, and avoid walking on materials that can be damaged or are unsafe underfoot.',
  'Countertop Materials': 'Use cutting boards and trivets, wipe spills promptly, and follow the fabricator\'s instructions for cleaners and sealing. Keep the product name, color, lot information, and installer documentation for matching repairs or warranty questions.'
}

export default {
  name: 'App',
  components: {
    ArrowRightIcon,
    BadgeDollarSignIcon,
    BookOpenIcon,
    CalculatorIcon,
    CheckCircle2Icon,
    Layers3Icon,
    MenuIcon,
    RulerIcon,
    XIcon,
    PageIntro,
    Breadcrumbs,
    FaqBlock,
    RelatedBlock
  },
  data() {
    return {
      site: siteData.site,
      calculators: siteData.calculators,
      guides: siteData.guides,
      materials: siteData.materials,
      costGuides: siteData.costGuides,
      faqs: siteData.faqs,
      currentPath: normalizePath(window.location.pathname),
      mobileOpen: false,
      calculatorForms: this.createCalculatorForms(siteData.calculators),
      homeBudget: {
        area: 250,
        costPerSqFt: 90,
        contingency: 15
      },
      contact: {
        name: '',
        email: '',
        topic: '',
        message: '',
        website: ''
      },
      contactSending: false,
      contactStatus: ''
    }
  },
  computed: {
    route() {
      const path = normalizePath(this.currentPath)
      const parts = path.split('/').filter(Boolean)

      if (path === '/') return { type: 'home' }
      if (path === '/calculators') return { type: 'calculators' }
      if (path === '/guides') return { type: 'guides' }
      if (path === '/materials') return { type: 'materials' }
      if (path === '/cost-guides') return { type: 'costs' }
      if (path === '/faq') return { type: 'faq' }
      if (path === '/about') return { type: 'about' }
      if (path === '/editorial-standards') return { type: 'editorial' }
      if (path === '/contact') return { type: 'contact' }
      if (path === '/privacy-policy') return { type: 'privacy' }
      if (path === '/terms-of-service') return { type: 'terms' }

      if (parts[0] === 'calculators') {
        return { type: 'calculator', item: this.findBySlug(this.calculators, parts[1]) }
      }
      if (parts[0] === 'guides') {
        return { type: 'guide', item: this.findBySlug(this.guides, parts[1]) }
      }
      if (parts[0] === 'materials') {
        return { type: 'material', item: this.findBySlug(this.materials, parts[1]) }
      }
      if (parts[0] === 'cost-guides') {
        return { type: 'cost', item: this.findBySlug(this.costGuides, parts[1]) }
      }
      if (parts[0] === 'faq') {
        return { type: 'faqDetail', item: this.findBySlug(this.faqs, parts[1]) }
      }

      return { type: 'notFound' }
    },
    groupedCalculators() {
      return groupBy(this.calculators, 'category')
    },
    groupedGuides() {
      return groupBy(this.guides, 'category')
    },
    groupedMaterials() {
      return groupBy(this.materials, 'category')
    },
    popularCalculators() {
      return siteData.popularCalculatorSlugs
        .map(slug => this.findBySlug(this.calculators, slug))
        .filter(Boolean)
        .map(item => ({
          ...item,
          url: `/calculators/${item.slug}`
        }))
    },
    featuredGuides() {
      return siteData.featuredGuideSlugs
        .map(slug => this.findBySlug(this.guides, slug))
        .filter(Boolean)
    },
    homeEstimate() {
      const base = Number(this.homeBudget.area || 0) * Number(this.homeBudget.costPerSqFt || 0)
      const contingency = base * Number(this.homeBudget.contingency || 0) / 100
      return {
        base: money(base),
        contingency: money(contingency),
        total: money(base + contingency)
      }
    },
    calculatorResult() {
      if (this.route.type !== 'calculator' || !this.route.item) {
        return null
      }
      return runCalculator(this.route.item.formulaKey, this.calculatorForms[this.route.item.slug])
    },
    breadcrumbs() {
      if (!this.route.item && this.route.type !== 'faqDetail') {
        return [{ label: 'Home', url: '/' }]
      }
      const map = {
        calculator: { label: 'Calculators', url: '/calculators' },
        guide: { label: 'Guides', url: '/guides' },
        material: { label: 'Materials', url: '/materials' },
        cost: { label: 'Cost Guides', url: '/cost-guides' },
        faqDetail: { label: 'FAQ', url: '/faq' }
      }
      const parent = map[this.route.type]
      const current = this.route.type === 'faqDetail' ? this.route.item.question : this.route.item.title
      return [
        { label: 'Home', url: '/' },
        parent,
        { label: current, url: this.currentPath }
      ].filter(Boolean)
    },
    pageMeta() {
      const titleSuffix = '| Renovation Paths'
      const defaults = {
        title: 'Free Home Improvement Calculators & Planning Tools | Renovation Paths',
        description: 'Calculate renovation materials, estimate costs, compare home improvement materials, and plan projects with Renovation Paths.'
      }

      if (this.route.type === 'calculator' && this.route.item) {
        return { title: this.route.item.seoTitle, description: this.route.item.metaDescription }
      }
      if (this.route.type === 'guide' && this.route.item) {
        return { title: this.route.item.seoTitle, description: this.route.item.metaDescription }
      }
      if (this.route.type === 'material' && this.route.item) {
        return { title: this.route.item.seoTitle, description: this.route.item.metaDescription }
      }
      if (this.route.type === 'cost' && this.route.item) {
        return { title: this.route.item.seoTitle, description: this.route.item.metaDescription }
      }
      if (this.route.type === 'faqDetail' && this.route.item) {
        return { title: this.route.item.seoTitle, description: this.route.item.metaDescription }
      }

      const staticMeta = {
        calculators: ['Home Improvement Calculators | Renovation Paths', 'Browse free renovation calculators for paint, flooring, tile, concrete, roofing, outdoor materials, and remodeling budgets.'],
        guides: ['Home Improvement Guides | Renovation Paths', 'Read practical guides for painting, flooring, kitchens, bathrooms, roofing, outdoor projects, budgeting, permits, and maintenance.'],
        materials: ['Home Improvement Material Database | Renovation Paths', 'Compare renovation materials by cost, durability, maintenance, advantages, disadvantages, and best use cases.'],
        costs: ['Home Renovation Cost Guides | Renovation Paths', 'Plan home renovation costs with average ranges, material and labor notes, price factors, and budget tips.'],
        faq: ['Home Improvement FAQ | Renovation Paths', 'Answers to common home renovation questions about materials, costs, installation, maintenance, and planning.'],
        about: [`About Us ${titleSuffix}`, 'Learn how Renovation Paths helps homeowners plan home improvement projects with calculators, guides, material data, and cost references.'],
        editorial: [`Editorial Standards And Methodology ${titleSuffix}`, 'Learn how Renovation Paths tests calculators, explains cost ranges, reviews project guidance, handles independence, and corrects content.'],
        contact: [`Contact ${titleSuffix}`, `Contact Renovation Paths at ${this.site.contactEmail} for questions, corrections, and feedback.`],
        privacy: [`Privacy Policy ${titleSuffix}`, 'Privacy Policy covering Google AdSense, cookies, analytics, third-party advertising, server logs, contact form data, and email correspondence.'],
        terms: [`Terms of Service ${titleSuffix}`, 'Terms of Service for using Renovation Paths calculators, guides, material data, and cost planning information.']
      }

      if (staticMeta[this.route.type]) {
        return {
          title: staticMeta[this.route.type][0],
          description: staticMeta[this.route.type][1]
        }
      }

      return defaults
    }
  },
  watch: {
    currentPath() {
      this.updateMeta()
      this.mobileOpen = false
    }
  },
  mounted() {
    window.addEventListener('popstate', this.syncPath)
    this.updateMeta()
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.syncPath)
  },
  methods: {
    createCalculatorForms(items) {
      return items.reduce((forms, item) => {
        forms[item.slug] = item.fields.reduce((values, field) => {
          values[field.key] = field.default
          return values
        }, {})
        return forms
      }, {})
    },
    findBySlug(items, slug) {
      return items.find(item => item.slug === slug)
    },
    syncPath() {
      this.currentPath = normalizePath(window.location.pathname)
    },
    navigate(path) {
      const normalized = normalizePath(path)
      if (normalized !== this.currentPath) {
        window.history.pushState({}, '', normalized)
        this.currentPath = normalized
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
      this.mobileOpen = false
    },
    updateMeta() {
      document.documentElement.lang = 'en'
      document.title = this.pageMeta.title
      this.setMeta('description', this.pageMeta.description)
      this.setMeta('robots', ['faqDetail', 'notFound'].includes(this.route.type) ? 'noindex,follow' : 'index,follow')
      const normalizedPath = normalizePath(this.currentPath)
      this.setCanonical(`${this.site.domain}${normalizedPath === '/' ? '/' : `${normalizedPath}/`}`)
    },
    setMeta(name, content) {
      let tag = document.querySelector(`meta[name="${name}"]`)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('name', name)
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', content)
    },
    setCanonical(url) {
      let tag = document.querySelector('link[rel="canonical"]')
      if (!tag) {
        tag = document.createElement('link')
        tag.setAttribute('rel', 'canonical')
        document.head.appendChild(tag)
      }
      tag.setAttribute('href', url)
    },
    resourceDescription(item) {
      return item.description || item.summary || item.overview || item.averageCost || item.answer || ''
    },
    resourceUrl(type, slug) {
      const map = {
        Calculator: 'calculators',
        Guide: 'guides',
        Material: 'materials',
        'Cost Guide': 'cost-guides',
        FAQ: 'faq'
      }
      return `/${map[type]}/${slug}`
    },
    findResource(slug) {
      const sources = [
        { type: 'Calculator', items: this.calculators },
        { type: 'Guide', items: this.guides },
        { type: 'Material', items: this.materials },
        { type: 'Cost Guide', items: this.costGuides },
        { type: 'FAQ', items: this.faqs }
      ]
      for (let index = 0; index < sources.length; index += 1) {
        const found = this.findBySlug(sources[index].items, slug)
        if (found) {
          return {
            type: sources[index].type,
            title: found.title || found.question,
            description: this.resourceDescription(found),
            url: this.resourceUrl(sources[index].type, found.slug)
          }
        }
      }
      return null
    },
    relatedItems(slugs) {
      return (slugs || [])
        .map(slug => this.findResource(slug))
        .filter(Boolean)
        .slice(0, 6)
    },
    calculatorAssumption(item) {
      const inputs = item.fields.map(field => field.label.toLowerCase()).join(', ')
      return `The ${item.title} applies the displayed formula to the values you enter for ${inputs}. It assumes the measured areas or lengths describe the work accurately and that repeated units use the same dimensions. Openings, irregular shapes, unusable offcuts, product defects, and site conditions are included only when you add them to a measurement or allowance. The result is a planning quantity, not a supplier takeoff or site inspection.`
    },
    calculatorCategoryNote(category) {
      return calculatorCategoryNotes[category] || calculatorCategoryNotes['General Home Projects']
    },
    guideBeforeStart(item) {
      const firstStep = item.steps[0] || 'Define the project scope.'
      const secondStep = item.steps[1] || 'Verify the existing conditions.'
      const materials = (item.materials || []).slice(0, 3).join(', ').toLowerCase()
      return `Begin ${item.title.toLowerCase()} by documenting the existing condition, desired finish, dimensions, and work boundaries. The first two checkpoints are: ${firstStep} ${secondStep} Confirm that ${materials || 'the selected materials'} are compatible with the substrate and one another. Record product names, installation instructions, lead times, return rules, and safety data before scheduling labor or removing anything that protects the home from weather or water.`
    },
    guideProfessionalNote(category) {
      return professionalNotes[category] || professionalNotes.Planning
    },
    guideDecisionPoints(item) {
      const steps = item.steps || []
      const materials = item.materials || []
      return [
        `Scope boundary: decide what is repaired, replaced, protected, and explicitly excluded before pricing ${item.title.toLowerCase()}.`,
        `Sequence: complete "${steps[0] || 'verify conditions'}" before "${steps[1] || 'order materials'}" so hidden work does not invalidate later finishes.`,
        `Material compatibility: verify the instructions, lead time, warranty, and required accessories for ${materials.slice(0, 3).join(', ').toLowerCase() || 'the primary products'}.`,
        `Completion standard: write down acceptable color, alignment, tolerances, cleanup, inspection, and warranty documentation before the work begins.`
      ]
    },
    materialDecisionGuide(item) {
      const bestUses = item.bestUseCases.slice(0, 2).join(' and ').toLowerCase()
      return `${item.title} is best evaluated as a system rather than by appearance alone. Its stated cost is ${item.averageCost}, while the expected durability is ${item.durability}. Compare those figures with installation preparation, repair options, cleaning requirements, moisture or heat exposure, and the expected length of ownership. It is commonly considered for ${bestUses}. A strong fit is one where the product limitations are acceptable in the actual room and the installer can meet every substrate and warranty requirement.`
    },
    materialBuyingQuestions(item) {
      return [
        `Does the quoted ${item.averageCost.toLowerCase()} include underlayment, adhesives, trim, delivery, waste, tax, preparation, and disposal?`,
        `Can the supplier document the product's ${item.durability.toLowerCase()} and the maintenance required to preserve its warranty?`,
        `Which site condition could turn the advantage "${item.advantages[0]}" into a poor fit, and how will that condition be verified?`,
        `How will the installer manage "${item.disadvantages[0]}" and leave matching material or repair information for future use?`
      ]
    },
    materialCategoryNote(category) {
      return materialCategoryNotes[category] || 'Follow the product manufacturer\'s maintenance, cleaning, storage, and inspection instructions. Keep purchase records and a small labeled sample where practical so future repairs can match the original specification.'
    },
    costRangeExplanation(item) {
      return `${item.averageCost} is an early planning range for ${item.title.toLowerCase()}, expressed in U.S. dollars and not adjusted to a specific ZIP code or current contractor schedule. The material context is ${item.materialCost.toLowerCase()}, while the labor context is ${item.laborCost.toLowerCase()}. Use the range to test scope and affordability, then replace each allowance with written local supplier or contractor pricing. The low and high ends are not promises: access, existing damage, design complexity, permits, finish level, and regional demand can move a real quote outside the range.`
    },
    costAssumptions(item) {
      return [
        `The estimate assumes a defined ${item.category.toLowerCase()} scope with ordinary access and no major concealed structural, moisture, utility, or hazardous-material problem.`,
        `Material pricing should be checked against the stated scope: ${item.materialCost}`,
        `Labor pricing should be checked against the stated scope: ${item.laborCost}`,
        `Taxes, design fees, permits, delivery, temporary facilities, protection, cleanup, and disposal may be separate unless a quote includes them in writing.`,
        `A contingency is a reserve for documented uncertainty, not a reason to accept an incomplete scope or an unexplained allowance.`
      ]
    },
    submitContact() {
      this.contactSending = true
      this.contactStatus = ''

      fetch('/backend/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(this.contact)
      })
        .then(async response => {
          const data = await response.json().catch(() => ({}))
          if (!response.ok || !data.success) {
            throw new Error(data.error || 'Message could not be sent.')
          }
          this.contactStatus = 'Thanks. Your message has been received.'
          this.contact = { name: '', email: '', topic: '', message: '', website: '' }
        })
        .catch(error => {
          this.contactStatus = `${error.message} Please email ${this.site.contactEmail}.`
        })
        .finally(() => {
          this.contactSending = false
        })
    }
  }
}
</script>
